// src/components/SessionSelector.tsx (FULL FIXED)
// Fixes:
// - Don't set err/disable UI just because token isn't ready yet (avoid "No token" poisoning)
// - Properly surface real HTTP errors (resp.ok check + text)
// - Keeps your existing API + sorting + UX

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { trimTrailingSlash } from "../lib/url";

type SessionItem = {
  session_id: string;
  session_date: string;
};

type Props = {
  selected: string;
  onChange: (id: string, session?: SessionItem) => void;
  apiBaseUrl: string;
  getToken: () => Promise<string>;
  patientId?: string | null;
};

const SENTINEL = "current";

const SessionSelector: React.FC<Props> = ({
  selected,
  onChange,
  apiBaseUrl,
  getToken,
  patientId,
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<SessionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Stabilize getToken so fetchList doesn't recreate on every token refresh
  const getTokenRef = useRef(getToken);
  useEffect(() => { getTokenRef.current = getToken; }, [getToken]);

  const patientSelected = Boolean(patientId?.trim());

  const fetchList = useCallback(async () => {
    if (!patientSelected || !apiBaseUrl) return;

    setLoading(true);
    setErr(null);

    try {
      const token = await getTokenRef.current();

      // ✅ Token not ready yet → don't mark as error (that disables the selector)
      // Just stop this attempt; next re-render / patient change will retry.
      if (!token) {
        return;
      }

      const base = trimTrailingSlash(apiBaseUrl);
      const url = new URL(`${base}/sessions/list`);
      url.searchParams.set("patient_id", String(patientId));
      url.searchParams.set("limit", "50");

      const resp = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });

      // ✅ Real HTTP errors should be shown
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `HTTP ${resp.status}`);
      }

      const data = await resp.json();
      const list: SessionItem[] = Array.isArray(data?.items) ? data.items : [];

      list.sort((a, b) => b.session_date.localeCompare(a.session_date));

      if (mounted.current) setItems(list);
    } catch (e: any) {
      if (mounted.current) {
        setErr(e?.message || t("sessionSelector.failedToLoad"));
        setItems([]);
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [apiBaseUrl, patientId, patientSelected]);

  useEffect(() => {
    if (patientSelected) void fetchList();
  }, [fetchList, patientSelected]);

  // Listen for external refresh events (e.g., after session deletion)
  useEffect(() => {
    const handler = () => {
      if (mounted.current) void fetchList();
    };
    window.addEventListener("tj:sessions:refresh", handler);
    return () => window.removeEventListener("tj:sessions:refresh", handler);
  }, [fetchList]);

  const options = useMemo(() => {
    if (!patientSelected) {
      return [{ value: SENTINEL, label: t("sessionSelector.selectPatientFirst") }];
    }

    // Optional: if there was an error, show it as the first option label
    const errPrefix = err ? [{ value: SENTINEL, label: t("sessionSelector.errorLoading") }] : [];

    return [
      ...errPrefix,
      { value: SENTINEL, label: t("sessionSelector.currentSession") },
      ...items.map((s) => ({
        value: s.session_id,
        label: s.session_date,
      })),
    ];
  }, [items, patientSelected, err, t]);

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <label style={{ fontWeight: 800 }}>{t("sessionSelector.label")}</label>

      <select
        className="headerSelect"
        value={selected}
        // ✅ Don’t disable on err; keep it usable (you can still pick "Current Session")
        disabled={!patientSelected || loading}
        onChange={(e) => {
          const id = e.target.value;
          onChange(id, items.find((s) => s.session_id === id));
        }}
      >
        {options.map((o) => (
          <option key={o.value + o.label} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// getToken/onChange change reference on every parent render but the
// component only uses them inside effects/handlers — skip in compare.
export default React.memo(SessionSelector, (prev, next) =>
  prev.selected === next.selected &&
  prev.apiBaseUrl === next.apiBaseUrl &&
  prev.patientId === next.patientId
);
